import type { TreeApi, TreeEvidenceApi, TreeEventApi } from "@/lib/api";
import { formatUtm, toUtm22S } from "@/lib/utm";
import type { ArborInspectionDetail, ArborInspectionSummary } from "@/types/arboricultural-inspection";
import type { EventoTimelineFCA, FCAData, VistoriaFCA, InterferenciaFCA } from "@/types/fca";

const INSPECTION_TYPE_LABELS: Record<string, string> = {
  INVENTORY_BASELINE: "Inventário",
  ARBORICULTURAL_TECHNICAL: "Vistoria Técnica",
  MONITORING: "Monitoramento",
  POST_MANAGEMENT: "Pós-manejo",
  EMERGENCY: "Emergencial",
  SUPERVISION: "Supervisão",
  OTHER: "Outro",
};

const RECOMMENDATION_TYPE_LABELS: Record<string, string> = {
  PODA_LIMPEZA: "Poda de limpeza",
  PODA_FORMACAO: "Poda de formação",
  PODA_ADEQUACAO: "Poda de adequação",
  PODA_EMERGENCIAL: "Poda emergencial",
  SUPRESSAO: "Supressão",
  TRANSPLANTE: "Transplante",
  TRATAMENTO_FITOSSANITARIO: "Tratamento fitossanitário",
  MONITORAMENTO: "Monitorar",
  REPLANTIO: "Replantio",
  SEM_INTERVENCAO: "Sem intervenção",
};

const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Rascunho",
  IN_PROGRESS: "Em andamento",
  SUBMITTED: "Submetida",
  UNDER_REVIEW: "Em revisão",
  RETURNED: "Devolvida",
  VALIDATED: "Concluída",
  CONCLUDED: "Concluída",
  CANCELLED: "Cancelada",
};

const INTERFERENCE_TYPE_LABELS: Record<string, string> = {
  OVERHEAD_NETWORK: "Rede aérea",
  SIDEWALK: "Calçada",
  TRAFFIC_VISIBILITY: "Visibilidade de tráfego",
  LIGHTING: "Iluminação",
  BUILDING: "Edificação",
  UNDERGROUND_INFRASTRUCTURE: "Infraestrutura subterrânea",
  URBAN_FURNITURE: "Mobiliário urbano",
  OTHER: "Outro",
};

const F_LABEL: Record<string, string> = {
  BOM: "F1 — Boa / vigorosa",
  REGULAR: "F2 — Regular",
  RUIM: "F3 — Ruim / declínio",
  CRITICO: "F4 — Morte iminente",
  MORTO: "F4 — Morta",
};

const R_LABEL: Record<string, string> = {
  R1: "R1 — Raízes subterrâneas",
  R2: "R2 — Superficiais, dentro da área disponível",
  R3: "R3 — Superficiais, além da área disponível",
  R4: "R4 — Superficiais, com dano ou conflito",
};

const A_LABEL: Record<string, string> = {
  A0: "A0 — Nenhum alvo relevante",
  A1: "A1 — Baixa exposição",
  A2: "A2 — Alvo relevante",
};

/** Indicador em tres estados: nao avaliado nunca e exibido como ausente. */
function tri(value: boolean | null | undefined): string {
  if (value === true) return "Presente";
  if (value === false) return "Ausente";
  return "Não avaliado";
}

const EVENT_KIND: Record<string, { tipo: string; titulo: string; icone: string }> = {
  CADASTRO_INICIAL: { tipo: "cadastro", titulo: "Cadastro do exemplar", icone: "ri-map-pin-add-line" },
  ATUALIZACAO_CADASTRAL: { tipo: "alteracao", titulo: "Correção de cadastro", icone: "ri-edit-line" },
  VISTORIA_TECNICA: { tipo: "vistoria", titulo: "Vistoria técnica validada", icone: "ri-shield-check-line" },
  PODA_RECOMENDADA: { tipo: "manejo", titulo: "Poda recomendada", icone: "ri-scissors-2-line" },
  PODA_REALIZADA: { tipo: "manejo", titulo: "Poda realizada", icone: "ri-scissors-2-line" },
  SUPRESSAO_AUTORIZADA: { tipo: "manejo", titulo: "Supressão autorizada", icone: "ri-delete-bin-line" },
  SUPRESSAO_EXECUTADA: { tipo: "manejo", titulo: "Supressão executada", icone: "ri-delete-bin-line" },
  REPLANTIO: { tipo: "manejo", titulo: "Replantio", icone: "ri-plant-line" },
  TRATAMENTO_FITOSSANITARIO: { tipo: "manejo", titulo: "Tratamento fitossanitário", icone: "ri-medicine-bottle-line" },
  INTERFERENCIA_REGISTRADA: { tipo: "vistoria", titulo: "Interferência registrada", icone: "ri-flashlight-line" },
  ALTERACAO_RISCO: { tipo: "vistoria", titulo: "Alteração de risco", icone: "ri-alert-line" },
  ALTERACAO_STATUS: { tipo: "alteracao", titulo: "Alteração de situação", icone: "ri-swap-line" },
  QUEDA_ARVORE: { tipo: "ocorrencia", titulo: "Queda de árvore", icone: "ri-error-warning-line" },
  DANO_TEMPESTADE: { tipo: "ocorrencia", titulo: "Dano por tempestade", icone: "ri-thunderstorms-line" },
};

export function mapTimelineEvents(events: TreeEventApi[]): EventoTimelineFCA[] {
  return events.map((e) => {
    const kind = EVENT_KIND[e.event_type] ?? { tipo: "outro", titulo: e.event_type, icone: "ri-time-line" };
    return {
      id: String(e.id),
      data: formatDateISO(e.event_date),
      tipo: kind.tipo,
      titulo: kind.titulo,
      descricao: e.description ?? "",
      // Eventos anteriores a 26/10/2026 nao guardam o autor; nao se inventa um.
      tecnico: e.actor_name ?? "Autor não registrado",
      icone: kind.icone,
    };
  });
}

const INDIVIDUAL_TYPE_LABELS: Record<string, string> = {
  ARVORE: "Árvore",
  PALMEIRA: "Palmeira",
  ARBUSTO: "Arbusto",
  TOCO: "Toco",
  MUDA: "Muda",
  ARVORE_MORTA: "Árvore morta",
  CEPA: "Cepa",
};

function individualTypeLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return INDIVIDUAL_TYPE_LABELS[code] ?? code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, " ");
}

function formatDateISO(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  return dateStr.slice(0, 10);
}

function mapInspectionSummaryToVistoria(s: ArborInspectionSummary): VistoriaFCA {
  const primaryRecommendation = RECOMMENDATION_TYPE_LABELS[s.riskPriority ?? ""] ?? "—";
  return {
    id: String(s.id),
    data: formatDateISO(s.inspectionDate),
    tipo: INSPECTION_TYPE_LABELS[s.inspectionType] ?? s.inspectionType,
    tecnico: s.inspectorName ?? "—",
    estadoFito: s.healthStatus?.toLowerCase() ?? "",
    risco: s.riskClass?.toLowerCase() ?? "",
    dap: s.dapCm ?? 0,
    altura: s.heightM ?? 0,
    recomendacao: primaryRecommendation,
    status: WORKFLOW_STATUS_LABELS[s.workflowStatus] ?? s.workflowStatus,
    observacao: s.notes ?? "",
  };
}

function mapInterferencesToFCA(detail: ArborInspectionDetail): InterferenciaFCA[] {
  return detail.interferences.map((inf) => ({
    id: String(inf.id),
    tipo: INTERFERENCE_TYPE_LABELS[inf.interferenceType ?? ""] ?? (inf.interferenceType ?? "—"),
    intensidade: inf.intensity ?? "—",
    descricao: inf.description ?? "",
    fotoVinculada: null,
    recomendacao: "—",
  }));
}

export function buildFCADataFromApi(
  tree: TreeApi,
  inspections: ArborInspectionSummary[],
  latestDetail: ArborInspectionDetail | null,
  timelineEvents: TreeEventApi[] = [],
  treeEvidence: TreeEvidenceApi[] = [],
): FCAData {
  const m = latestDetail?.measurement;
  const ha = latestDetail?.healthAssessment;
  const ra = latestDetail?.riskAssessment;
  const mainRec = latestDetail?.recommendations?.[0];

  return {
    id: String(tree.id),
    codigoCta: tree.cta_code ?? `CTA-${tree.id}`,
    nomePopular: tree.species_popular_name ?? "Não identificada",
    nomeCientifico: tree.species_scientific_name ?? "—",
    tipo: individualTypeLabel(tree.individual_type),
    statusRevisao: tree.review_status ?? "cadastrada",
    estadoFitossanitario: (ha?.generalStatus ?? "").toLowerCase(),
    classeRisco: (ra?.riskClass ?? "").toLowerCase(),
    fotoPrincipal: "",
    dap: m?.dapCm ?? 0,
    altura: m?.heightM ?? 0,
    diametroCopa: m?.crownDiameterM ?? 0,
    ultimaVistoria: formatDateISO(latestDetail?.inspectionDate ?? tree.latest_inspection_date),
    fotosCount: treeEvidence.filter((e) => !e.invalidated_at).length,
    manejoRecomendado: RECOMMENDATION_TYPE_LABELS[mainRec?.recommendationType ?? ""] ?? (mainRec?.recommendationType ?? "—"),
    municipio: tree.municipality_name ?? "—",
    uf: tree.municipality_uf ?? "—",
    bairro: tree.neighborhood ?? "—",
    logradouro: tree.street ?? "—",
    numero: tree.address_reference ?? "—",
    latitude: tree.latitude != null ? String(tree.latitude) : "—",
    longitude: tree.longitude != null ? String(tree.longitude) : "—",
    srid: "EPSG:4326 (WGS 84, graus)",
    ...utmFields(tree),
    projectId: tree.project_id,
    projetoVinculado: tree.project_name ?? "—",
    areaVerdeVinculada: tree.spatial_unit_name,
    dadosBotanicos: {
      tipoIndividuo: individualTypeLabel(tree.individual_type),
      nomePopular: tree.species_popular_name ?? "—",
      nomeCientifico: tree.species_scientific_name ?? "—",
      familiaBotanica: tree.botanical_family ?? "—",
      origem: "—",
      porte: "—",
      estagioDesenvolvimento: "—",
      observacoesBotanicas: latestDetail?.botanicalNotes ?? "—",
    },
    fitossanidade: {
      classeF: ha?.generalStatus ? (F_LABEL[ha.generalStatus] ?? ha.generalStatus) : "—",
      classeR: ha?.rootCondition ? (R_LABEL[ha.rootCondition] ?? ha.rootCondition) : "Não classificada",
      classeA: ra?.targetClass ? (A_LABEL[ra.targetClass] ?? ra.targetClass) : "Não classificada",
      notasRaizes: ha?.rootNotes ?? "",
      // A condicao atual so existe a partir de uma vistoria VALIDADA: rascunho e vistoria em
      // revisao nao sao o estado do exemplar, so trabalho em andamento.
      semVistoriaValidada: latestDetail === null,
      estadoGeral: ha?.generalStatus ? (F_LABEL[ha.generalStatus] ?? ha.generalStatus) : "—",
      condicaoCopa: "—",
      condicaoTronco: "—",
      condicaoRaizes: ha?.rootCondition ? (R_LABEL[ha.rootCondition] ?? ha.rootCondition) : (ha?.exposedRootsDetail ?? "Não avaliada"),
      pragas: tri(ha?.pests),
      doencas: tri(ha?.diseases),
      fungos: tri(ha?.fungi),
      cupins: tri(ha?.termites),
      cavidades: tri(ha?.cavities),
      podridao: tri(ha?.rot),
      galhosSecos: tri(ha?.dryBranches),
      inclinacao:
        ha?.abnormalInclination === true
          ? `${ha.inclinationAngleDeg ?? "?"}° ${ha.inclinationDirection ?? ""}`.trim()
          : ha?.abnormalInclination === false
            ? "Normal"
            : "Não avaliado",
      raizesExpostas: tri(ha?.exposedRoots),
      classeRisco: ra?.riskClass ?? "—",
      criteriosRisco: Object.keys(ra?.riskCriteria ?? {}).filter((k) => k !== "target_class" && k !== "catalogo"),
      parecerTecnico: latestDetail?.technicalOpinion ?? "—",
      recomendacao: RECOMMENDATION_TYPE_LABELS[mainRec?.recommendationType ?? ""] ?? "—",
    },
    vistorias: inspections.map(mapInspectionSummaryToVistoria),
    fotos: [],
    interferencias: latestDetail ? mapInterferencesToFCA(latestDetail) : [],
    manejo: {
      recomendacaoAtual: RECOMMENDATION_TYPE_LABELS[mainRec?.recommendationType ?? ""] ?? "—",
      prioridade: mainRec?.priority ?? "—",
      justificativa: mainRec?.justification ?? "—",
      status: mainRec?.status ?? "—",
      historico: (latestDetail?.recommendations ?? []).map((r) => ({
        id: String(r.id),
        data: formatDateISO(r.createdAt),
        tipo: RECOMMENDATION_TYPE_LABELS[r.recommendationType ?? ""] ?? (r.recommendationType ?? "—"),
        status: r.status ?? "—",
        tecnico: latestDetail?.inspectorName ?? "—",
        descricao: r.justification ?? "",
      })),
    },
    documentos: [],
    timeline: mapTimelineEvents(timelineEvents),
    alertas: buildAlerts(tree, latestDetail),
    tecnicoResponsavel: latestDetail?.inspectorName ?? "—",
    dataCadastro: formatDateISO(tree.created_at),
  };
}

function buildAlerts(tree: TreeApi, detail: ArborInspectionDetail | null): FCAData["alertas"] {
  const alerts: FCAData["alertas"] = [];
  if (!detail) {
    alerts.push({
      tipo: "sem-validacao",
      mensagem: "Nenhuma vistoria validada: a condição atual do exemplar ainda não foi homologada.",
      nivel: "info",
    });
  }
  const riskClass = detail?.riskAssessment?.riskClass;
  if (riskClass === "CRITICO" || riskClass === "ALTO") {
    alerts.push({
      tipo: "risco",
      mensagem: `Classificação de risco ${riskClass === "CRITICO" ? "CRÍTICO" : "ALTO"} — verificar recomendações de manejo.`,
      nivel: riskClass === "CRITICO" ? "danger" : "warning",
    });
  }
  if (detail?.requiresMonitoring) {
    alerts.push({
      tipo: "monitoramento",
      mensagem: "Esta árvore requer monitoramento periódico conforme a última inspeção.",
      nivel: "info",
    });
  }
  if (!tree.species_scientific_name) {
    alerts.push({
      tipo: "identificacao",
      mensagem: "Identificação botânica pendente — espécie não confirmada.",
      nivel: "warning",
    });
  }
  return alerts;
}

/** UTM do servidor; calculado aqui so se a API ainda nao o enviar. */
function utmFields(tree: TreeApi): { utmE: string; utmN: string } {
  let e = tree.utm_e ?? null;
  let n = tree.utm_n ?? null;
  if ((e == null || n == null) && tree.latitude != null && tree.longitude != null) {
    const r = toUtm22S(Number(tree.latitude), Number(tree.longitude));
    e = r.e;
    n = r.n;
  }
  return { utmE: formatUtm(e), utmN: formatUtm(n) };
}
